"use client";

import { useEffect } from "react";

function getMaxScroll(): number {
  const doc = document.documentElement;
  return Math.max(1, doc.scrollHeight - window.innerHeight);
}

/**
 * Preserves proportional scroll progress (scrollY / maxScroll) across window
 * resizes so vh-based scrollytelling sections do not jump to a different moment.
 */
export function usePreserveScrollOnResize(): void {
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let lastMax = getMaxScroll();
    let lastWidth = window.innerWidth;
    let savedRatio: number | null = null;
    let endTimer: ReturnType<typeof setTimeout> | undefined;

    const onScroll = () => {
      if (savedRatio !== null) return;
      lastScrollY = window.scrollY;
      lastMax = getMaxScroll();
    };

    const onResize = () => {
      const widthChanged = window.innerWidth !== lastWidth;
      lastWidth = window.innerWidth;
      if (!widthChanged) return;

      if (savedRatio === null) {
        savedRatio = lastScrollY / lastMax;
      }

      const newMax = getMaxScroll();
      window.scrollTo(0, savedRatio * newMax);

      if (endTimer !== undefined) clearTimeout(endTimer);
      endTimer = setTimeout(() => {
        savedRatio = null;
        lastScrollY = window.scrollY;
        lastMax = getMaxScroll();
        endTimer = undefined;
      }, 200);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (endTimer !== undefined) clearTimeout(endTimer);
    };
  }, []);
}
